import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Send, 
  Trash2, 
  Calculator, 
  Variable, 
  Shapes, 
  Sigma,
  Sparkles,
  User,
  ChevronDown,
  Loader2,
  RefreshCw,
  Volume2,
  VolumeX,
  Pause,
  Play,
  Mic,
  Zap,
  LogIn,
  Crown
} from "lucide-react";
import { formatMathText } from "../utils/mathFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = {
  arithmetic: { name: "Arithmetic", icon: Calculator, color: "icon-arithmetic" },
  algebra: { name: "Algebra", icon: Variable, color: "icon-algebra" },
  geometry: { name: "Geometry", icon: Shapes, color: "icon-geometry" },
  calculus: { name: "Calculus", icon: Sigma, color: "icon-calculus" },
  general: { name: "General Math", icon: Sparkles, color: "bg-primary/10 text-primary" }
};

const exampleQuestions = {
  arithmetic: [
    "What is 15% of 240?",
    "Simplify the fraction 48/64",
    "Calculate 3/4 + 2/5"
  ],
  algebra: [
    "Solve for x: 2x + 5 = 13",
    "Factor x² - 9",
    "Solve the system: x + y = 10, x - y = 4"
  ],
  geometry: [
    "Find the area of a circle with radius 7",
    "Calculate the hypotenuse of a right triangle with legs 3 and 4",
    "What is the volume of a sphere with radius 5?"
  ],
  calculus: [
    "Find the derivative of f(x) = x³ + 2x",
    "Calculate the integral of 2x dx",
    "Find the limit as x approaches 0 of sin(x)/x"
  ],
  general: [
    "Help me understand prime numbers",
    "What is the Pythagorean theorem?",
    "Explain the order of operations"
  ]
};

export default function ChatTutor() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  
  const currentTopic = topicId && topics[topicId] ? topicId : "general";
  const topicInfo = topics[currentTopic];
  const TopicIcon = topicInfo.icon;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Audio state
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [loadingAudioId, setLoadingAudioId] = useState(null);
  const [tutorModeEnabled, setTutorModeEnabled] = useState(false);
  const audioRef = useRef(null);

  // Subscription state
  const [usageInfo, setUsageInfo] = useState(null);
  const [limitReached, setLimitReached] = useState(false);

  // Require authentication
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast.error("Please sign in to use the AI Tutor");
      navigate("/auth", { state: { from: { pathname: `/chat/${topicId || ''}` } } });
    }
  }, [isAuthenticated, authLoading, navigate, topicId]);

  // Fetch usage info
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUsage = async () => {
      try {
        const res = await axios.get(`${API}/subscriptions/usage`);
        setUsageInfo(res.data);
        const q = res.data.question;
        setLimitReached(!q.allowed);
      } catch { /* ignore for unauthenticated */ }
    };
    fetchUsage();
  }, [isAuthenticated, messages.length]);

  // Load chat history on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await axios.get(`${API}/chat/history/${sessionId}`);
        if (response.data.messages && response.data.messages.length > 0) {
          setMessages(response.data.messages);
        }
      } catch (error) {
        console.error("Error loading history:", error);
      }
    };
    loadHistory();
  }, [sessionId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    // Create placeholder for streaming response
    const assistantMessageId = `assistant-${Date.now()}`;
    const assistantMessage = {
      id: assistantMessageId,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
      isStreaming: true,
      cached: false
    };
    setMessages((prev) => [...prev, assistantMessage]);

    try {
      // Use streaming endpoint
      const response = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(axios.defaults.headers.common['Authorization'] && {
            'Authorization': axios.defaults.headers.common['Authorization']
          }),
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage.content,
          topic: currentTopic,
          stream: true
        })
      });

      if (response.status === 403) {
        const err = await response.json();
        toast.error(err.detail || "Daily limit reached. Upgrade your plan!");
        setLimitReached(true);
        setMessages((prev) => prev.filter((m) => m.id !== userMessage.id && m.id !== assistantMessageId));
        setInput(userMessage.content);
        setLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let isCached = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                toast.error(data.error);
                break;
              }

              if (data.content) {
                fullContent += data.content;
                isCached = data.cached || false;
                
                // Update message content in real-time
                setMessages((prev) => 
                  prev.map((msg) => 
                    msg.id === assistantMessageId 
                      ? { ...msg, content: fullContent, cached: isCached }
                      : msg
                  )
                );
              }

              if (data.done) {
                // Mark streaming as complete
                setMessages((prev) => 
                  prev.map((msg) => 
                    msg.id === assistantMessageId 
                      ? { ...msg, isStreaming: false, id: data.message_id || assistantMessageId }
                      : msg
                  )
                );
                
                // Show cache indicator
                if (isCached) {
                  toast.success("⚡ Instant response from cache!", { duration: 2000 });
                }
                
                // Auto-play audio if tutor mode is enabled
                if (tutorModeEnabled && fullContent) {
                  setTimeout(() => {
                    playAudio(data.message_id || assistantMessageId, fullContent);
                  }, 500);
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to get response. Please try again.");
      // Remove both messages if request failed
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id && m.id !== assistantMessageId));
      setInput(userMessage.content);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/chat/history/${sessionId}`);
      setMessages([]);
      toast.success("Chat cleared");
    } catch (error) {
      toast.error("Failed to clear chat");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleExampleClick = (question) => {
    setInput(question);
    textareaRef.current?.focus();
  };

  // Audio playback function
  const playAudio = async (messageId, text) => {
    // If already playing this message, stop it
    if (playingAudioId === messageId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudioId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setLoadingAudioId(messageId);

    try {
      const response = await axios.post(
        `${API}/tts`,
        { text, voice: "nova" },
        { responseType: "blob" }
      );

      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingAudioId(null);
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        toast.error("Failed to play audio");
        setPlayingAudioId(null);
      };

      await audio.play();
      setPlayingAudioId(messageId);
    } catch (error) {
      console.error("TTS error:", error);
      if (error.response?.status === 403) {
        toast.error("Text-to-Speech requires a Pro or Premium plan.", { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      } else {
        toast.error("Failed to generate audio");
      }
    } finally {
      setLoadingAudioId(null);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="h-[calc(100vh-8rem)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="h-[calc(100vh-8rem)] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-slate-200 dark:border-slate-800">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold font-['Outfit'] mb-2">
              Sign In Required
            </h2>
            <p className="text-muted-foreground mb-6">
              Please create an account or sign in to access the AI Math Tutor.
            </p>
            <Link to="/auth" state={{ from: { pathname: `/chat/${topicId || ''}` } }}>
              <Button className="rounded-full px-8">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In to Continue
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-transition h-[calc(100vh-8rem)] sm:h-[calc(100vh-8rem)] flex flex-col">
      <div className="max-w-4xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6 flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6 shrink-0 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${topicInfo.color} flex items-center justify-center shrink-0`}>
              <TopicIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold font-['Outfit'] truncate" data-testid="chat-title">
                AI Math Tutor
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">
                {topicInfo.name}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            {/* Tutor Voice Mode Toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full bg-muted/50 border border-border">
                    <Mic className={`w-4 h-4 ${tutorModeEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                    <Label htmlFor="tutor-mode" className="text-xs font-medium cursor-pointer hidden sm:inline">
                      Voice
                    </Label>
                    <Switch
                      id="tutor-mode"
                      checked={tutorModeEnabled}
                      onCheckedChange={setTutorModeEnabled}
                      className="scale-75"
                      data-testid="tutor-mode-toggle"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Auto-play voice explanations</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Topic Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full text-xs sm:text-sm" data-testid="topic-selector">
                  <span className="hidden sm:inline">Change Topic</span>
                  <span className="sm:hidden">Topic</span>
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="topic-dropdown">
                {Object.entries(topics).map(([id, topic]) => {
                  const Icon = topic.icon;
                  return (
                    <DropdownMenuItem key={id} asChild>
                      <Link 
                        to={id === "general" ? "/chat" : `/chat/${id}`}
                        className="flex items-center gap-2"
                        data-testid={`topic-option-${id}`}
                      >
                        <Icon className="w-4 h-4" />
                        {topic.name}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Clear Chat Button */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={clearChat}
              className="rounded-full w-8 h-8 sm:w-9 sm:h-9"
              data-testid="clear-chat-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Chat Area */}
        <Card className="flex-1 flex flex-col min-h-0 border-slate-200 dark:border-slate-800 overflow-hidden">
          <ScrollArea className="flex-1 p-3 sm:p-4" ref={scrollRef} data-testid="chat-messages">
            {messages.length === 0 ? (
              // Welcome State
              <div className="h-full flex flex-col items-center justify-center text-center p-4 sm:p-6">
                <div className={`w-16 h-16 rounded-2xl ${topicInfo.color} flex items-center justify-center mb-4`}>
                  <TopicIcon className="w-8 h-8" />
                </div>
                <h2 className="text-xl font-semibold font-['Outfit'] mb-2">
                  Ask me anything about {topicInfo.name}!
                </h2>
                <p className="text-muted-foreground mb-4 max-w-md">
                  I'll provide step-by-step solutions and clear explanations.
                </p>
                {tutorModeEnabled && (
                  <div className="flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm">
                    <Volume2 className="w-4 h-4" />
                    <span>Voice mode is ON - I'll explain out loud!</span>
                  </div>
                )}
                <p className="text-muted-foreground mb-4 text-sm">
                  Try one of these examples:
                </p>
                <div className="flex flex-wrap justify-center gap-2" data-testid="example-questions">
                  {exampleQuestions[currentTopic]?.map((question, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      className="rounded-full text-sm"
                      onClick={() => handleExampleClick(question)}
                      data-testid={`example-${idx}`}
                    >
                      {question}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              // Messages
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`chat-message flex gap-2 sm:gap-3 ${
                      message.role === "user" ? "justify-end" : ""
                    }`}
                    data-testid={`message-${message.role}`}
                  >
                    {message.role === "assistant" && (
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <Sparkles className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-foreground ${message.isStreaming ? 'animate-pulse' : ''}`} />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      {/* Cache indicator badge */}
                      {message.role === "assistant" && message.cached && !message.isStreaming && (
                        <div className="flex items-center gap-1 mb-2 text-xs text-green-600">
                          <Zap className="w-3 h-3" />
                          <span>Instant response</span>
                        </div>
                      )}
                      <p className={`whitespace-pre-wrap text-xs sm:text-sm leading-relaxed ${
                        message.role === "assistant" ? "math-content" : ""
                      }`}>
                        {message.role === "assistant" ? formatMathText(message.content) : message.content}
                        {message.isStreaming && <span className="inline-block w-2 h-4 ml-1 bg-primary animate-pulse" />}
                      </p>
                      {/* Audio button for assistant messages - only show when not streaming */}
                      {message.role === "assistant" && !message.isStreaming && message.content && (
                        <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-2">
                          <Button
                            variant={playingAudioId === message.id ? "default" : "ghost"}
                            size="sm"
                            onClick={() => playAudio(message.id, message.content)}
                            disabled={loadingAudioId === message.id}
                            className={`h-7 sm:h-8 px-2 sm:px-3 text-xs rounded-full ${
                              playingAudioId === message.id 
                                ? 'bg-primary text-primary-foreground animate-pulse' 
                                : 'hover:bg-background/50'
                            }`}
                            data-testid={`audio-btn-${message.id}`}
                          >
                            {loadingAudioId === message.id ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                <span className="hidden sm:inline">Generating...</span>
                                <span className="sm:hidden">Loading</span>
                              </>
                            ) : playingAudioId === message.id ? (
                              <>
                                <Volume2 className="w-3 h-3 mr-1 animate-pulse" />
                                <span className="hidden sm:inline">Speaking... (tap to stop)</span>
                                <span className="sm:hidden">Stop</span>
                              </>
                            ) : (
                              <>
                                <Volume2 className="w-3 h-3 mr-1" />
                                <span className="hidden sm:inline">Listen to explanation</span>
                                <span className="sm:hidden">Listen</span>
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                    {message.role === "user" && (
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent flex items-center justify-center shrink-0">
                        <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Loading indicator */}
                {loading && (
                  <div className="chat-message flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <Sparkles className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <div className="bg-muted rounded-2xl px-4 py-3">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="p-3 sm:p-4 border-t border-border shrink-0">
            {/* Usage limit banner */}
            {limitReached && (
              <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-between gap-3" data-testid="limit-banner">
                <div className="flex items-center gap-2 text-sm text-amber-800">
                  <Crown className="w-4 h-4 shrink-0" />
                  <span>Daily question limit reached. Upgrade for unlimited access.</span>
                </div>
                <Link to="/pricing">
                  <Button size="sm" className="rounded-full text-xs shrink-0" data-testid="upgrade-btn">Upgrade</Button>
                </Link>
              </div>
            )}
            {/* Usage counter */}
            {usageInfo?.question && !limitReached && usageInfo.question.remaining > 0 && usageInfo.question.remaining !== -1 && (
              <div className="mb-2 text-xs text-muted-foreground text-center" data-testid="usage-counter">
                {usageInfo.question.remaining} question{usageInfo.question.remaining !== 1 ? 's' : ''} remaining today
              </div>
            )}
            <div className="flex gap-2">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a math question..."
                className="min-h-[44px] sm:min-h-[52px] max-h-28 sm:max-h-32 resize-none rounded-xl text-sm"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || loading || limitReached}
                className="rounded-xl px-4 sm:px-6 shrink-0"
                data-testid="send-button"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center hidden sm:block">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
